import { HeaderNavigation } from "@faire/retailer-visitor-shared/components/Header/HeaderNavigation";
import { ProgressBar } from "@faire/retailer-visitor-shared/components/ProgressBar/ProgressBar";
import { getIsStaging } from "@faire/retailer-visitor-shared/serialized-data/getEnvName";
import { useSettingAndAssignSignUpSplit } from "@faire/retailer-visitor-shared/settings/useSettingSignUpSplit";
import { useSettingSkipOwFUpsell } from "@faire/retailer-visitor-shared/settings/useSettingSkipOwFUpsell";
import { useSignupProgression } from "@faire/retailer-visitor-shared/views/SignUp/_components/__internal__/useSignupProgression";
import { useSignUpNationality } from "@faire/retailer-visitor-shared/views/SignUp/_hooks/useSignUpNationality";
import { useIsAppRouterMigration } from "@faire/retailer-visitor-shared/views/SignUp/_utils/useIsAppRouterMigration";
import {
  Step,
  stepKeyToPathMap,
} from "@faire/retailer-visitor-shared/views/SignUp/Step";
import { useHandleOwFSkip } from "@faire/retailer-visitor-shared/views/SignUp/useHandleOwFSkip";
import bookACall from "@faire/web-api--source/routes/www/welcome/r/book-a-call";
import owfApplication from "@faire/web-api--source/routes/www/welcome/r/open-with-faire/application";
import owfIntro from "@faire/web-api--source/routes/www/welcome/r/open-with-faire/intro";
import owfStartShopping from "@faire/web-api--source/routes/www/welcome/r/open-with-faire/start-shopping";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { isLocal } from "@faire/web/common/user-agent/isLocal";
import { Column, HorizontalLine } from "@faire/web/slate/Layout";
import { removeLocaleFromUrl } from "@faire/web/ui/routing/util";
import React from "react";
import { useLocation } from "react-router-dom";
import { useHistory, matchPath } from "react-router-dom";

const backButtonBlockList = [
  Step.PERSONAL_INFO,
  Step.SELECT_NET_TERMS,
  Step.NOTIFY_ME_SUCCESS,
  Step.SCC_NET_TERMS,
];

export const SignUpHeader: React.FC = () => {
  const isAppRouterMigration = useIsAppRouterMigration();
  const isSignUpSplitEnabled = useSettingAndAssignSignUpSplit();
  const location = useLocation();
  const history = useHistory();
  const showHeader = useShouldShowHeader();
  const { signUpPercent, hideProgressBar } = useSignupProgression();
  const handleClose = useHandleOwFSkip();
  const isInSkipOwFUpsellXp = useSettingSkipOwFUpsell();
  const { isAmerican } = useSignUpNationality();

  // Only to differentiate while testing app router migration
  const shouldUseDifferentProgressBarColor =
    (getIsStaging() || isLocal()) && isAppRouterMigration;

  if (!showHeader) {
    return null;
  }

  const currentPath = removeLocaleFromUrl(location.pathname);
  const currentStep = Object.entries(stepKeyToPathMap).find(
    ([_, path]) => path() === currentPath
  )?.[0] as Step;

  const showBackButton = !backButtonBlockList.includes(currentStep);
  // Only americans see SCC Open With Faire app
  const shouldShowSkipCta =
    Step.OWF_APPLICATION === currentStep && !isAmerican && isInSkipOwFUpsellXp;
  const showOnCloseButton =
    [Step.OWF_UPSELL, isInSkipOwFUpsellXp ? undefined : Step.OWF_APPLICATION]
      .filter(isNotUndefined)
      .includes(currentStep) || shouldShowSkipCta;

  const onBack = () => {
    history.goBack();
  };

  return (
    <Column data-test-id="sign-up-header">
      <HeaderNavigation
        forceDirectLoad={isSignUpSplitEnabled}
        onBack={showBackButton && !showOnCloseButton ? onBack : undefined}
        onClose={showOnCloseButton ? handleClose : undefined}
        showSkipCta={shouldShowSkipCta}
      />
      {!hideProgressBar ? (
        <ProgressBar
          data-test-id="sign-up-progress-bar"
          value={signUpPercent}
          valueColor={shouldUseDifferentProgressBarColor ? "orange" : undefined}
        />
      ) : (
        <HorizontalLine />
      )}
    </Column>
  );
};

const useShouldShowHeader = () => {
  const location = useLocation();
  const shouldShowHeader = useSettingSkipOwFUpsell();
  const doNotShowHeaderPaths = [
    bookACall(),
    shouldShowHeader ? undefined : owfApplication(),
    owfIntro(),
    owfStartShopping(),
  ]
    .filter(isNotUndefined)
    .some((path) => matchPath(location.pathname, path));

  return !doNotShowHeaderPaths;
};
